/**
 * Iter 158 · Dunning Queue — reviews & approves overdue-invoice nudges.
 * Route: /app/finance/dunning
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Bell, RefreshCw, Check, X, Send as SendIcon, MessageCircle, Mail } from "lucide-react";

const TIER_COLOR = {
  soft: "bg-blue-100 text-blue-800",
  firm: "bg-amber-100 text-amber-800",
  escalate: "bg-orange-100 text-orange-800",
  final: "bg-red-100 text-red-800",
};

function inr(n) {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
}

export default function DunningQueue() {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState("draft");
  const [data, setData] = useState({ rows: [], stats: {} });
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const r = await api.get(`/workspaces/${activeWorkspaceId}/dunning/queue?status=${status}`);
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId, status]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/dunning/generate`, {});
      toast.success(`Generated ${r.data.generated} new draft(s), kept ${r.data.existing_kept}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Generate failed")); }
    finally { setBusy(false); }
  };

  const approve = async (id) => {
    try { await api.patch(`/dunning/${id}/approve`); toast.success("Approved"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this dunning draft?")) return;
    try { await api.patch(`/dunning/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); }
  };
  const send = async (id) => {
    try { await api.post(`/dunning/${id}/send`); toast.success("Sent — tracking active"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
  };
  const edit = async (id, payload) => {
    try { await api.patch(`/dunning/${id}`, payload); toast.success("Saved"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="dunning-page">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Bell className="h-3 w-3" /> Collections
          </div>
          <h1 className="text-3xl font-black">Dunning Queue</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review AI-drafted reminders before they go out. Suggest → Review → Approve → Send.
          </p>
        </div>
        <Button onClick={generate} disabled={busy} className="rounded-none" data-testid="dunning-generate">
          <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />
          Generate drafts
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Drafts to review" value={data.stats.draft || 0} tint="bg-blue-50" testid="stat-draft" />
        <StatTile label="Approved" value={data.stats.approved || 0} tint="bg-amber-50" testid="stat-approved" />
        <StatTile label="Sent" value={data.stats.sent || 0} tint="bg-emerald-50" testid="stat-sent" />
        <StatTile label="Cancelled" value={data.stats.cancelled || 0} tint="bg-gray-50" testid="stat-cancelled" />
        <StatTile label="Total balance" value={inr(data.total_at_risk || 0)} tint="bg-red-50" testid="stat-balance" />
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="rounded-lg">
          <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled</TabsTrigger>
        </TabsList>
        <TabsContent value={status} className="pt-4">
          {data.rows.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              No {status} dunning drafts. {status === "draft" && <button onClick={generate} className="text-primary underline">Generate now</button>}
            </div>
          ) : (
            <div className="space-y-3">
              {data.rows.map((row) => (
                <DunningCard
                  key={row.id}
                  row={row}
                  expanded={expanded === row.id}
                  onExpand={() => setExpanded(expanded === row.id ? null : row.id)}
                  onApprove={() => approve(row.id)}
                  onCancel={() => cancel(row.id)}
                  onSend={() => send(row.id)}
                  onEdit={(p) => edit(row.id, p)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatTile({ label, value, tint, testid }) {
  return (
    <div className={`rounded-lg p-3 border border-border ${tint}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-mono font-bold">{value}</div>
    </div>
  );
}

function DunningCard({ row, expanded, onExpand, onApprove, onCancel, onSend, onEdit }) {
  const [subject, setSubject] = useState(row.subject || "");
  const [body, setBody] = useState(row.body || "");
  const [whatsapp, setWhatsapp] = useState(row.whatsapp || "");
  const [emails, setEmails] = useState((row.to_emails || []).join(", "));
  const [dirty, setDirty] = useState(false);

  const save = () => {
    onEdit({ subject, body, whatsapp, to_emails: emails.split(",").map((s) => s.trim()).filter(Boolean) });
    setDirty(false);
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`dunning-card-${row.id}`}>
      <button onClick={onExpand} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/30">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${TIER_COLOR[row.tier] || "bg-gray-100"}`}>{row.tier}</span>
        <div className="flex-1">
          <div className="font-semibold text-sm">{row.client_name} · {row.doc_no}</div>
          <div className="text-xs text-muted-foreground">Balance {inr(row.balance_due)} · {row.days_overdue}d overdue</div>
        </div>
        <div className="flex items-center gap-1">
          {row.channel === "both" ? (
            <><Mail className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /></>
          ) : row.channel === "whatsapp" ? <MessageCircle className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
        </div>
        {row.status === "draft" && (
          <>
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onCancel(); }} className="h-7 w-7 p-0" data-testid={`dunning-cancel-${row.id}`}>
              <X className="h-3.5 w-3.5 text-red-500" />
            </Button>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onApprove(); }} className="h-7 rounded-none" data-testid={`dunning-approve-${row.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
          </>
        )}
        {row.status === "approved" && (
          <Button size="sm" onClick={(e) => { e.stopPropagation(); onSend(); }} className="h-7 rounded-none" data-testid={`dunning-send-${row.id}`}>
            <SendIcon className="h-3.5 w-3.5 mr-1" /> Send now
          </Button>
        )}
      </button>
      {expanded && (
        <div className="p-4 border-t border-border space-y-3 bg-muted/10">
          {row.status === "draft" && (
            <>
              <div>
                <Label className="text-xs">To emails</Label>
                <Input value={emails} onChange={(e) => { setEmails(e.target.value); setDirty(true); }} data-testid={`edit-emails-${row.id}`} />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} data-testid={`edit-subject-${row.id}`} />
              </div>
              <div>
                <Label className="text-xs">Email body</Label>
                <Textarea value={body} onChange={(e) => { setBody(e.target.value); setDirty(true); }} rows={6} data-testid={`edit-body-${row.id}`} />
              </div>
              <div>
                <Label className="text-xs">WhatsApp message</Label>
                <Textarea value={whatsapp} onChange={(e) => { setWhatsapp(e.target.value); setDirty(true); }} rows={3} data-testid={`edit-whatsapp-${row.id}`} />
              </div>
              {dirty && (
                <div className="flex justify-end">
                  <Button size="sm" onClick={save} className="rounded-none" data-testid={`edit-save-${row.id}`}>Save changes</Button>
                </div>
              )}
            </>
          )}
          {row.status !== "draft" && (
            <div className="space-y-2 text-xs">
              <div><b>Subject:</b> {row.subject}</div>
              <div className="whitespace-pre-line text-muted-foreground">{row.body}</div>
              {row.whatsapp && <div className="pt-2 border-t"><b>WhatsApp:</b> <span className="text-muted-foreground">{row.whatsapp}</span></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
